import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-tooltip.scss';
import type IdsPopup from '../ids-popup/ids-popup';
import '../ids-popup/ids-popup';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

export type IdsTooltipPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

const Base = IdsKeyboardMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Tooltip Component
 * @type {IdsTooltip}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 * @part tooltip - the tooltip container
 */
@customElement('ids-tooltip')
@scss(styles)
export default class IdsTooltip extends Base {
  constructor() {
    super();

    // Setup initial internal states
    this.state = {
      target: null,
      trigger: 'hover',
      visible: false,
      maxWidth: '',
      minWidth: '',
      showOnFocus: true
    };
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element,
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#updateAria();
    this.#attachEventHandlers();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  /**
   * Returns the properties/settings we handle as getters/setters
   * @returns {Array} The supported settings in an array
   */
  static get attributes(): Array<string> {
    return [
      attributes.DELAY,
      attributes.KEEP_OPEN,
      attributes.PLACEMENT,
      attributes.TARGET,
      attributes.TRIGGER,
      attributes.VISIBLE,
      attributes.MIN_WIDTH,
      attributes.MAX_WIDTH,
      attributes.OFFSET_CONTAINER,
      attributes.SHOW_ON_FOCUS
    ];
  }

  /**
   * Create the Template for the component contents
   * @returns {string} The template
   */
  template(): string {
    const cssParts = 'popup: tooltip-popup, arrow: tooltip-arrow, arrow-top: tooltip-arrow-top, arrow-right: tooltip-arrow-right, arrow-bottom: tooltip-arrow-bottom, arrow-left: tooltip-arrow-left';

    return `<ids-popup part="popup" center-on-overflow="true" id="${this.id || 'ids'}-tooltip" exportparts="${cssParts}">
        <div class="ids-tooltip" slot="content" part="tooltip">
          <span class="adjust-whitespace"><slot></slot></span>
        </div>
        </ids-popup>
      `;
  }

  /**
   * Bind Internal Event Handlers
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.detachAllEvents();
    if (!(typeof this.target === 'string')) {
      this.#bindEvents(this.target);
      return;
    }

    const list = document.querySelectorAll(this.target);
    for (let i = 0, len = list.length; i < len; i++) {
      this.#bindEvents(list[i]);
    }
  }

  /**
   * Bind the events to a tooltip target.
   * @param {HTMLElement} targetElem The element to attach events to
   * @private
   */
  #bindEvents(targetElem: any) {
    // Events to show on hover
    if (this.trigger === 'hover') {
      this.onEvent('hoverend.tooltip', targetElem, (e: Event) => {
        if (this.popup) {
          if (!this.popup?.alignTarget) this.popup.alignTarget = e.currentTarget as HTMLElement;
          this.visible = true;
        }
      }, { delay: this.delay });
      this.onEvent('mouseleave.tooltip', targetElem, () => {
        this.visible = false;
      });
      this.onEvent('click.tooltip', targetElem, () => {
        this.visible = false;
      });

      // Long Press
      this.onEvent('longpress.tooltip', targetElem, () => {
        this.visible = true;
      }, { delay: 1000 });

      this.onEvent('focusout.tooltip', targetElem, () => {
        this.visible = false;
      });

      this.onEvent('click.popup', this.popup, () => {
        this.visible = true;
      });
    }

    // Events to show on click
    if (this.trigger === 'click') {
      this.onEvent('click.tooltip', targetElem, (e: Event) => {
        if (this.popup) {
          this.popup.alignTarget = e.currentTarget as HTMLElement;
          if (this.visible) {
            this.visible = false;
            return;
          }
          this.visible = true;
        }
      });

      this.onEvent('click.popup', this.popup, () => {
        this.visible = false;
      });
    }

    // Events to show on focus
    if (this.trigger === 'focus') {
      this.onEvent('focusin.tooltip', targetElem, (e: Event) => {
        if (this.popup) {
          this.popup.alignTarget = e.currentTarget as HTMLElement;
          this.visible = true;
        }
      });

      this.onEvent('focusout.tooltip', targetElem, () => {
        this.visible = false;
      });
    }

    if (this.showOnFocus) {
      // Keyboard Focus event
      this.onEvent('keyboardfocus.tooltip', targetElem, () => {
        this.visible = true;
      });
    }
  }

  /**
   * Setup the popup
   * @private
   * @returns {void}
   */
  #configurePopup(): void {
    const popup = this.popup;

    const isInModal = getClosest(this, 'ids-modal');
    const isInActionPanel = getClosest(this, 'ids-action-panel');
    if (isInModal) {
      this.setAttribute(attributes.OFFSET_CONTAINER, 'ids-modal');
    }
    if (isInActionPanel) {
      this.setAttribute(attributes.OFFSET_CONTAINER, 'ids-action-panel');
    }

    // Popup settings / config
    if (popup) {
      popup.type = 'tooltip';
      popup.align = `${this.placement}, center`;
      popup.arrow = this.placement === 'center' ? 'bottom' : this.placement;
      popup.positionStyle = 'fixed';

      if (this.placement === 'top' || this.placement === 'bottom') {
        popup.setPosition(0, 2);
      }
      if (this.placement === 'left' || this.placement === 'right') {
        popup.setPosition(6, 0);
      }
    }
  }

  /**
   * Update the aria attributes with the correct contents
   * @private
   * @returns {void}
   */
  #updateAria(): void {
    // For ellipsis based tooltips we dont do this
    if (this.state?.noAria) {
      return;
    }

    const popup = this.popup;
    if (popup) {
      popup.alignTarget = typeof this.target === 'object'
        ? this.target
        : document.querySelectorAll(this.target)[0];

      const id = `${this.id || 'ids'}-tooltip`;
      const alignTarget = popup?.alignTarget?.querySelector(`#${id}`);
      if (alignTarget) {
        alignTarget.textContent = this.textContent;
        return;
      }

      if (popup.alignTarget) {
        const ariaSpan = `<ids-text id="${id}" audible="true">${this.textContent}</ids-text>`;
        popup.alignTarget.insertAdjacentHTML('beforeend', ariaSpan);
        popup.alignTarget.setAttribute('aria-describedby', `#${id}`);
      }
    }
  }

  /**
   * Show the tooltip (use visible for public API)
   * @private
   * @returns {Promise<void>}
   */
  async #show(): Promise<void> {
    // Trigger a veto-able `beforeshow` event.
    let canShow = true;
    const beforeShowResponse = (veto: any) => {
      canShow = !!veto;
    };

    // Trigger an async callback for contents
    if (this.state.beforeShow) {
      const stuff = await this.state.beforeShow();
      if (!stuff) return;
      this.textContent = stuff;
      this.#updateAria();
    }

    this.triggerEvent('beforeshow', this, {
      detail: {
        elem: this,
        response: beforeShowResponse
      }
    });

    if (!canShow) {
      this.visible = false;
      return;
    }

    // Show the popup
    this.#configurePopup();
    const popup = this.popup;
    if (popup) {
      popup.visible = true;
      popup.place();
      this.triggerEvent('show', this, { detail: { elem: this } });
      this.triggerEvent('aftershow', this, { detail: { elem: this } });
    }
  }

  /**
   * Show the tooltip  (use visible for public API)
   * @returns {void}
   */
  #hide(): void {
    const popup = this.popup;

    if (popup) {
      popup.visible = false;
      this.triggerEvent('hide', this, { detail: { elem: this } });
      this.triggerEvent('afterhide', this, { detail: { elem: this } });
      this.onHide();
    }
  }

  /**
   * update the popup style for a given property
   * @param {Record<string, string>} inlineStyles The style property to update
   */
  #updatePopupStyle(inlineStyles: Record<string, string>): void {
    const popup = this.popup;
    if (popup && popup.style) {
      Object.entries(inlineStyles).forEach(([property, value]) => {
        popup.style[property as any] = value || '';
      });
    }
  }

  /**
   * Callback for when hide is triggered
   * @returns {void}
   */
  onHide() {
  }

  /**
   * @readonly
   * @returns {IdsPopup | undefined | null} reference to the internal IdsPopup component
   */
  get popup(): IdsPopup | undefined | null {
    return this.shadowRoot?.querySelector('ids-popup');
  }

  /**
   * An async function that fires as the tooltip is showing allowing you to set contents.
   * @param {Function} func The async function
   */
  set beforeShow(func: () => Promise<string>) {
    this.state.beforeShow = func;
  }

  /**
   * Get the before show function
   * @returns {Function} the before show function
   */
  get beforeShow(): () => Promise<string> { return this.state.beforeShow; }

  /**
   * Set how long after hover you should delay before showing
   * @param {string | number} value The amount in ms to delay
   */
  set delay(value: string | number) {
    if (value) {
      this.setAttribute('delay', value.toString());
      return;
    }

    this.removeAttribute('delay');
  }

  get delay(): string | number { return Number(this.getAttribute('delay')) || 500; }

  /**
   * Sets the tooltip placement between left, right, top, bottom, center
   * @param {IdsTooltipPlacement} value The placement of the tooltip
   */
  set placement(value: IdsTooltipPlacement) {
    this.state.placement = value;

    if (value) {
      this.setAttribute('placement', value);
      return;
    }

    this.removeAttribute('placement');
  }

  get placement(): IdsTooltipPlacement { return (this.getAttribute('placement') || 'top') as IdsTooltipPlacement; }

  /**
   * Set the target element for the tooltip
   * @param {HTMLElement} value The target element selector
   */
  set target(value: any) {
    this.state.target = value;

    if (value && typeof value !== 'string') {
      this.#attachEventHandlers();
      return;
    }

    if (value && typeof value === 'string') {
      this.setAttribute('target', value);
      this.#attachEventHandlers();
      return;
    }

    this.removeAttribute('target');
    this.#attachEventHandlers();
  }

  get target(): any { return this.state.target; }

  /**
   * Set trigger agains the target between hover, click and focus
   * @param {string} value The trigger mode to use
   */
  set trigger(value: string) {
    this.state.trigger = value;

    if (this.state.trigger) {
      this.setAttribute('trigger', this.state.trigger);
      this.#attachEventHandlers();
      return;
    }

    this.removeAttribute('trigger');
    this.#attachEventHandlers();
  }

  get trigger(): string { return this.state.trigger || 'hover'; }

  /**
   * Set show behavior on focus event
   * @param {boolean | string} value The trigger mode to use
   */
  set showOnFocus(value: boolean | string) {
    this.state.showOnFocus = stringToBool(value);

    if (this.state.showOnFocus) {
      this.setAttribute('showOnFocus', this.state.showOnFocus);
      this.#attachEventHandlers();
      return;
    }

    this.setAttribute('showOnFocus', this.state.showOnFocus);
    this.#attachEventHandlers();
  }

  get showOnFocus(): boolean | string { return this.state.showOnFocus; }

  /**
   * Set the minimum width of the tooltip
   * @param { string } value The minimum width in px or percentage
   */
  set minWidth(value: string) {
    const minWidth = value?.toString() || '';
    this.state.minWidth = minWidth;
    this.#updatePopupStyle({ minWidth });

    if (minWidth) {
      this.setAttribute('min-width', minWidth);
    } else {
      this.removeAttribute('min-width');
    }
  }

  get minWidth() : string { return this.state.minWidth || ''; }

  /**
   * Set the maximum width of the tooltip
   * @param { string } value The maximum width in px or percentage
   */
  set maxWidth(value: string) {
    const maxWidth = value?.toString() || '';
    this.state.maxWidth = maxWidth;
    this.#updatePopupStyle({ maxWidth });

    if (maxWidth) {
      this.setAttribute('max-width', maxWidth);
    } else {
      this.removeAttribute('max-width');
    }
  }

  get maxWidth() : string { return this.state.maxWidth || ''; }

  /**
   * Sets offset container, such as a container with a `container-type
   * set which directly affects a fixed popup's position
   * @param {string|null} selector CSS selector of container
   */
  set offsetContainer(selector: string | null) {
    if (selector) {
      this.setAttribute(attributes.OFFSET_CONTAINER, selector);
      this.popup?.setAttribute(attributes.OFFSET_CONTAINER, selector);
    } else {
      this.removeAttribute(attributes.OFFSET_CONTAINER);
      this.popup?.removeAttribute(attributes.OFFSET_CONTAINER);
    }
  }

  /**
   * Gets offset container if any
   * @returns {string|null} CSS selector of container
   */
  get offsetContainer(): string | null {
    return this.getAttribute(attributes.OFFSET_CONTAINER);
  }

  /**
   * Check if any element in the tooltip content has overflow="ellipsis" or if it's plain text,
   * and update the span class accordingly
   * @private
   */
  #updateSpanClassForEllipsis(): void {
    // Check if any child has overflow="ellipsis" attribute
    const hasEllipsisChild = Array.from(this.children).some((child) => child.getAttribute('overflow') === 'ellipsis');

    // Check if content is plain text (no HTML elements)
    const textContent = this.textContent || '';
    const isPlainText = this.children.length === 0 && textContent.trim().length > 0;

    // Find the span with adjust-whitespace class
    const span = this.shadowRoot?.querySelector('.ids-tooltip span');
    if (span) {
      // Remove the class if there's an ellipsis child or if it's plain text
      if (hasEllipsisChild || isPlainText) {
        span.classList.remove('adjust-whitespace');
      } else {
        span.classList.add('adjust-whitespace');
      }
    }
  }

  /**
   * Set tooltip immediately to visible/invisible
   * @param {boolean | string} value The target element selector
   */
  set visible(value: boolean | string) {
    const trueVal = stringToBool(value);
    if (this.state.visible !== trueVal) {
      const popup = this.popup;
      this.state.visible = trueVal;

      if (popup && !popup.alignTarget) {
        popup.alignTarget = typeof this.target === 'object'
          ? this.target
          : document.querySelectorAll(this.target)[0];
      }

      // If a text area change align target to the container and adjust
      if (popup && popup.alignTarget?.nodeName === 'IDS-TEXTAREA') {
        popup.alignTarget = popup.alignTarget.shadowRoot?.querySelector<any>('textarea');
        popup.onPlaceEnd = (popupRect: any, domElement: any) => {
          domElement.style.top = `${parseInt(domElement.style.top) - 14}px`;
          return domElement;
        };
      }

      if (this.state.visible) {
        // Check for ellipsis content and update span class before showing
        this.#updateSpanClassForEllipsis();

        this.setAttribute('visible', 'true');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.#show();
        return;
      }

      if (popup) popup.alignTarget = null;
      this.removeAttribute('visible');
      this.#hide();
    }
  }

  get visible(): boolean | string { return this.state.visible; }
}
